import * as React from "react";
import { Helmet } from "react-helmet-async";
import { baseMetadata, generatePageMetadata, siteConfig } from "@/lib/seo/metadata";

type PageMetadata = ReturnType<typeof generatePageMetadata>;

type SeoHeadProps = {
  metadata: PageMetadata;
  structuredData?: Record<string, unknown>[];
};

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

function resolveTitle(title: PageMetadata["title"]) {
  if (typeof title === "string") {
    const template =
      typeof baseMetadata.title === "object" && baseMetadata.title?.template ? baseMetadata.title.template : null;
    return template ? template.replace("%s", title) : title;
  }

  if (title && typeof title === "object") {
    if (title.default) {
      return title.default;
    }
    if (title.template) {
      return title.template.replace("%s", siteConfig.name);
    }
  }

  return siteConfig.title;
}

function toAbsoluteUrl(pathOrUrl?: string) {
  if (!pathOrUrl) {
    return undefined;
  }
  if (ABSOLUTE_URL_PATTERN.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const baseUrl = siteConfig.url.replace(/\/+$/, "");
  const normalizedPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${baseUrl}${normalizedPath}`;
}

function formatRobots(robots?: unknown) {
  if (!robots) {
    return undefined;
  }

  if (typeof robots === "string") {
    return robots;
  }

  if (Array.isArray(robots)) {
    return robots.join(", ");
  }

  if (typeof robots !== "object") {
    return undefined;
  }

  const directives = Object.entries(robots).flatMap(([key, value]) => {
    if (value == null) {
      return [];
    }

    if (typeof value === "boolean") {
      const prefix = key.toLowerCase();
      if (value) {
        return prefix;
      }
      if (prefix.startsWith("no")) {
        return prefix;
      }
      return `no${prefix}`;
    }

    if (Array.isArray(value)) {
      return value.map((item) => `${key.toLowerCase()}:${item}`);
    }

    if (typeof value === "object") {
      // Nested directives (e.g., googleBot) are handled in dedicated meta tags.
      return [];
    }

    return `${key.toLowerCase()}:${value}`;
  });

  return directives.length > 0 ? directives.join(", ") : undefined;
}

function formatFormatDetection(formatDetection?: PageMetadata["formatDetection"]) {
  if (!formatDetection) {
    return undefined;
  }

  const directives = Object.entries(formatDetection).map(([key, enabled]) => {
    const normalizedKey = key.toLowerCase();
    return `${normalizedKey}=${enabled ? "yes" : "no"}`;
  });

  return directives.join(", ");
}

function getAuthors(metadata: PageMetadata) {
  if (!metadata.authors || metadata.authors.length === 0) {
    return undefined;
  }
  return metadata.authors
    .map((author) => author.name)
    .filter(Boolean)
    .join(", ");
}

export const SeoHead = ({ metadata, structuredData = [] }: SeoHeadProps) => {
  const title = resolveTitle(metadata.title);
  const robots = formatRobots(metadata.robots);
  const googleBot =
    typeof metadata.robots === "object" && metadata.robots !== null && !Array.isArray(metadata.robots)
      ? formatRobots((metadata.robots as { googleBot?: unknown }).googleBot)
      : undefined;
  const formatDetection = formatFormatDetection(metadata.formatDetection);

  /**
   * Drop a `noindex` this component does not own, once the page it sits on is indexable.
   *
   * The SPA fallback shell ships a literal `<meta name="robots" content="noindex">` (see
   * `buildFallbackShell` in `scripts/prerender.ts`) so an unknown URL served with a 200 cannot be
   * indexed. Helmet only manages the tags it created, so after a client-side navigation off a
   * fallback-served URL onto a public page, that literal tag is still in the document alongside
   * the page's own `index, follow` — and robots directives combine restrictively, so the live
   * document reads as noindex.
   *
   * Only indexable pages clear it: a page that is itself `noindex` leaves the marker alone, so
   * nothing private loses its protection.
   */
  React.useEffect(() => {
    if (!robots || robots.includes("noindex")) {
      return;
    }
    for (const stale of document.head.querySelectorAll('meta[name="robots"]:not([data-rh])')) {
      stale.remove();
    }
  }, [robots]);
  const author = getAuthors(metadata);
  const canonicalUrl = metadata.alternates?.canonical;

  const ogImages = metadata.openGraph?.images?.map((image) => ({
    ...image,
    url: toAbsoluteUrl(image.url),
  }));
  const twitterImages = metadata.twitter?.images?.map(toAbsoluteUrl).filter(Boolean);
  const jsonLdScripts = structuredData.map((schema) => ({
    type: "application/ld+json",
    innerHTML: JSON.stringify(schema),
  }));

  return (
    <Helmet prioritizeSeoTags script={jsonLdScripts}>
      <title>{title}</title>

      {metadata.description ? <meta name="description" content={metadata.description} /> : null}
      {metadata.keywords?.length ? <meta name="keywords" content={metadata.keywords.join(", ")} /> : null}
      {author ? <meta name="author" content={author} /> : null}
      {metadata.creator ? <meta name="creator" content={metadata.creator} /> : null}
      {metadata.publisher ? <meta name="publisher" content={metadata.publisher} /> : null}
      {metadata.category ? <meta name="category" content={metadata.category} /> : null}
      {formatDetection ? <meta name="format-detection" content={formatDetection} /> : null}

      {robots ? <meta name="robots" content={robots} /> : null}
      {googleBot ? <meta name="googlebot" content={googleBot} /> : null}

      {metadata.verification
        ? Object.entries(metadata.verification).map(([service, token]) =>
            typeof token === "string" && token ? <meta key={service} name={service} content={token} /> : null,
          )
        : null}

      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      {/* Open Graph */}
      {metadata.openGraph?.title ? <meta property="og:title" content={metadata.openGraph.title} /> : null}
      {metadata.openGraph?.description ? (
        <meta property="og:description" content={metadata.openGraph.description} />
      ) : null}
      {metadata.openGraph?.type ? <meta property="og:type" content={metadata.openGraph.type} /> : null}
      {metadata.openGraph?.url ? <meta property="og:url" content={metadata.openGraph.url} /> : null}
      {metadata.openGraph?.siteName ? <meta property="og:site_name" content={metadata.openGraph.siteName} /> : null}
      {metadata.openGraph?.locale ? <meta property="og:locale" content={metadata.openGraph.locale} /> : null}
      {/*
        `article:*` only means anything alongside `og:type=article`, which `generatePageMetadata`
        sets for exactly the pages that pass article metadata. Dates are what let a crawler treat
        the page as a dated piece of writing rather than another marketing page.
      */}
      {metadata.openGraph?.article ? (
        <meta property="article:published_time" content={metadata.openGraph.article.publishedTime} />
      ) : null}
      {metadata.openGraph?.article ? (
        <meta property="article:modified_time" content={metadata.openGraph.article.modifiedTime} />
      ) : null}
      {/*
        A profile URL, not a display name: the Open Graph article namespace defines `author` as a
        URL identifying the author's profile, so consumers discard a bare name. The organization
        wrote these, so its own LinkedIn page is that profile.
      */}
      {metadata.openGraph?.article ? (
        <meta property="article:author" content={siteConfig.socialProfiles.linkedin} />
      ) : null}
      {metadata.openGraph?.article?.section ? (
        <meta property="article:section" content={metadata.openGraph.article.section} />
      ) : null}
      {metadata.openGraph?.article?.tags?.map((tag) => (
        <meta property="article:tag" content={tag} key={`article-tag-${tag}`} />
      ))}

      {ogImages
        ? ogImages.map((image, index) => <meta property="og:image" content={image.url} key={`og-image-${index}`} />)
        : null}
      {ogImages
        ? ogImages.flatMap((image, index) => {
            const tags = [];
            if (image.width) {
              tags.push(
                <meta key={`og-image-${index}-width`} property="og:image:width" content={image.width.toString()} />,
              );
            }
            if (image.height) {
              tags.push(
                <meta key={`og-image-${index}-height`} property="og:image:height" content={image.height.toString()} />,
              );
            }
            if (image.alt) {
              tags.push(<meta key={`og-image-${index}-alt`} property="og:image:alt" content={image.alt} />);
            }
            return tags;
          })
        : null}

      {/* Twitter */}
      {metadata.twitter?.card ? <meta name="twitter:card" content={metadata.twitter.card} /> : null}
      {metadata.twitter?.title ? <meta name="twitter:title" content={metadata.twitter.title} /> : null}
      {metadata.twitter?.description ? (
        <meta name="twitter:description" content={metadata.twitter.description} />
      ) : null}
      {metadata.twitter?.site ? <meta name="twitter:site" content={metadata.twitter.site} /> : null}
      {metadata.twitter?.creator ? <meta name="twitter:creator" content={metadata.twitter.creator} /> : null}
      {twitterImages.length > 0
        ? twitterImages.map((image, index) => (
            <meta
              key={`twitter-image-${index}`}
              name={index === 0 ? "twitter:image" : `twitter:image${index + 1}`}
              content={image}
            />
          ))
        : null}
    </Helmet>
  );
};
